import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { ToastRegion } from 'qw-ui-lib-v2';
import { Navigate, Outlet } from 'react-router';
import cx from 'classnames';
import Loader from '../../views/common/Loader';
import AppErrorInfoDialog from '../../views/common/AppErrorInfoDialog';
import { bindDispatch } from '../../common/utils/redux';
import * as uiHelper from '../../common/utils/uiHelper';
import {
  disablePageScroll,
  enablePageScroll,
} from '../../common/utils/uiHelper';
import * as cookie from '../../common/utils/cookie';
import * as routePath from '../../common/utils/routePath';
import * as enums from '../../common/utils/enums';
import { getRegistrationApproval } from '../../common/utils/approvals';
import { isMaintenanceModeEnabled } from '../../common/utils/helpers';
import { withRouter } from '../../common/hoc/withRouter';
import Header from './Header';
import LandingHeader from './LandingHeader';
import NoAccessHeader from './NoAccessHeader';
import Footer from './Footer';
import '../css/app.css';
import '../css/type-scale.css';
import '../css/react-select.css';
import '../css/library.scss';
import '../css/ui.scss';

class Layout extends React.Component {
  componentDidMount() {
    const {
      actions,
      history: { replace },
    } = this.props;
    if (isMaintenanceModeEnabled()) {
      replace(routePath.maintenanceRoute);
      return;
    }
    setTimeout(actions.loadApp, 0);
  }

  componentDidUpdate() {
    const { app, location } = this.props;

    if (isMaintenanceModeEnabled()) {
      return;
    }

    if (app.initialized && location.pathname === routePath.rootRoute) {
      this.redirectBasedOnUserAuthorizationStatus();
    }

    if (this.props.app.error) {
      this.showErrorDialog(this.props.app.error);
    }
  }

  redirectBasedOnUserAuthorizationStatus() {
    const {
      app,
      user,
      history: { push },
    } = this.props;

    const authorizationStatus = cookie.get(
      cookie.KEY_USER_AUTHORIZATION_STATUS,
    );

    switch (authorizationStatus) {
      case cookie.USER_AUTHORIZATION_STATUS.NO_ACCESS: {
        push(routePath.noAccessRoute);
        return;
      }

      case cookie.USER_AUTHORIZATION_STATUS.NOT_FOUND: {
        push(routePath.notFoundRoute);
        return;
      }

      case cookie.USER_AUTHORIZATION_STATUS.AUTHORIZED: {
        // If the app is not initialized there is no point in routing to any of the routes
        if (!app.initialized) {
          return;
        }

        const lastNavigatedPath = cookie.get(cookie.KEY_LAST_NAVIGATED_PATH);
        const partner = user.partner;

        if (lastNavigatedPath && lastNavigatedPath !== routePath.rootRoute) {
          push(lastNavigatedPath);
        } else if (
          partner &&
          [enums.PartnerTypes.MANUAL, enums.PartnerTypes.OFFLINE].includes(
            partner.type,
          )
        ) {
          push(routePath.workOrdersRoute);
        } else {
          push(routePath.registerStep1Route);
        }
        return;
      }

      case cookie.USER_AUTHORIZATION_STATUS.UNAUTHORIZED:
      default:
        push(routePath.homeRoute);
        return;
    }
  }

  isAppBusy() {
    const { app } = this.props;
    app.isBusy ? disablePageScroll() : enablePageScroll();
    return app.isBusy;
  }

  showErrorDialog(errorInfo) {
    if (this.appErrorInfoDialog) {
      this.appErrorInfoDialog.handleOpen(errorInfo);
    }
  }

  shouldRedirectToHome() {
    const authorizationStatus = cookie.get(
      cookie.KEY_USER_AUTHORIZATION_STATUS,
    );

    return (
      authorizationStatus === cookie.USER_AUTHORIZATION_STATUS.UNAUTHORIZED &&
      this.props.location.pathname !== routePath.homeRoute
    );
  }

  getHeaderComponent() {
    const { location, driveTypes, inventory, workOrders } = this.props;

    if (isMaintenanceModeEnabled()) {
      return <NoAccessHeader />;
    }

    const authorizationStatus = cookie.get(
      cookie.KEY_USER_AUTHORIZATION_STATUS,
    );

    if (
      location.pathname === routePath.homeRoute ||
      location.pathname === '/' ||
      authorizationStatus === cookie.USER_AUTHORIZATION_STATUS.UNAUTHORIZED
    ) {
      return <LandingHeader />;
    } else if (location.pathname === routePath.noAccessRoute) {
      return <NoAccessHeader />;
    } else {
      const registrationApproval = getRegistrationApproval(
        this.props.approvals,
      );
      return (
        <Header
          user={this.props.user}
          inventory={inventory}
          driveTypes={driveTypes}
          registrationApproval={registrationApproval}
          saveDriveInventory={this.props.actions.saveDriveInventory}
          workOrders={workOrders}
        />
      );
    }
  }

  render() {
    const { app, actions, location } = this.props;
    const headerComponent = this.getHeaderComponent();

    if (!app.initialized && !isMaintenanceModeEnabled()) {
      return (
        <div style={uiHelper.PAGE_LOADER_STYLE}>
          <Loader size={80} />
        </div>
      );
    }

    return (
      <>
        <Helmet>
          <title>Qube Wire Partners</title>
        </Helmet>
        <div
          className={cx('app__container', {
            app__container__landing_page:
              location.pathname === routePath.homeRoute,
          })}
        >
          {headerComponent}
          <div className="container" id="container">
            {this.shouldRedirectToHome() ? (
              <Navigate to={routePath.homeRoute} replace />
            ) : (
              <Suspense
                fallback={
                  <div className="lazy-loading">
                    <Loader size={80} />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            )}
          </div>
          <Footer location={location} />
          <AppErrorInfoDialog
            ref={(component) => (this.appErrorInfoDialog = component)}
            showAppError={actions.showAppError}
          />
          <ToastRegion />
        </div>
        {this.isAppBusy() && (
          <div style={uiHelper.PAGE_LOADER_STYLE}>
            <Loader size={80} />
          </div>
        )}
      </>
    );
  }
}

Layout.propTypes = {
  children: PropTypes.node,
  actions: PropTypes.object.isRequired,
  initialized: PropTypes.bool,
};

const selector = createSelector(
  (state) => state.app,
  (state) => state.account.user,
  (state) => state.approvals,
  (state) => state.inventory,
  (state) => state.workOrders,
  (app, user, approvals, inventory, workOrders) => ({
    app,
    user,
    approvals,
    inventory,
    workOrders,
  }),
);

export default withRouter(connect(selector, bindDispatch)(Layout));
